import { Controller } from "@hotwired/stimulus"

// Device Login Controller
//
// Drives the QR device-login flow for the web requester.
//
// Lifecycle:
//   connect()       → try sessionStorage resume, otherwise createSession()
//   createSession() → POST /device/session, render SVG, start polling
//   poll()          → GET /api/v1/device_login/sessions/:token (every 1.5s)
//     ├─ pending    → tick countdown
//     ├─ scanned    → show "scanned" overlay (QR dimmed underneath)
//     ├─ authorized → consume() immediately, skip next tick
//     ├─ declined   → terminal "declined" overlay + Retry button
//     └─ expired    → terminal "expired" overlay + Retry button
//   consume()       → POST /device/consume, location.assign(redirect_to)
//
// Edge cases handled:
//   - Tab hidden: pause polling to save battery + load; resume with an
//     immediate poll on visibilitychange.
//   - Network error: exponential backoff 3s → 30s; UI stays on QR with an
//     inline "reconnecting" line (no layout jump).
//   - Client clock drift: countdown uses server expires_at as ground truth.
//   - Refresh: sessionStorage stashes { token, secret, expires_at }; on
//     connect we try the existing session first. If the server says it's
//     terminal we clear and create a new one.
//   - restart(): user-driven refresh; always clears storage, creates fresh.
//
// Security notes:
//   - session_secret is only in sessionStorage (tab-scoped) and in the JS
//     closure. Never logged, never goes to disk.
//   - All POST calls include the CSRF token from <meta name="csrf-token">.
export default class extends Controller {
  static targets = [
    "stage",
    "loading",
    "qr",
    "scannedOverlay",
    "consumingOverlay",
    "terminalOverlay",
    "terminalIcon",
    "terminalMessage",
    "refreshButton",
    "countdownRow",
    "countdown",
    "instructions"
  ]

  static values = {
    createUrl:  String,
    consumeUrl: String,
    i18n:       Object
  }

  // Polling cadence (ms). Server hint lands on the session response as
  // poll_interval (seconds); we respect that but enforce a floor to avoid
  // hammering if someone mis-configures the constant.
  static POLL_FLOOR_MS = 1000
  static POLL_MAX_MS   = 5000

  // Exponential backoff when the poll endpoint errors. Caps at 30s so a
  // flapping network still lets the user know something's happening.
  static BACKOFF_INITIAL_MS = 3000
  static BACKOFF_MAX_MS     = 30000

  static STORAGE_KEY = "babylog.device_login"

  connect() {
    this.state        = "idle"    // idle | pending | scanned | consuming | declined | expired | error
    this.pollTimer    = null
    this.countdownTimer = null
    this.backoffMs    = 0
    this.session      = null      // { token, secret, expires_at, poll_interval_ms, poll_url }

    this.visibilityHandler = this.onVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.visibilityHandler)

    this.boot()
  }

  disconnect() {
    this.clearPoll()
    this.clearCountdown()
    document.removeEventListener("visibilitychange", this.visibilityHandler)
  }

  // ------------- Lifecycle -------------

  async boot() {
    const stashed = this.loadStash()
    if (stashed && this.isStashUsable(stashed)) {
      // Resume first; fall through to createSession if server rejects.
      this.session = stashed
      this.showPending()
      const ok = await this.pollOnce()
      if (ok) {
        this.schedulePoll()
        this.startCountdown()
        return
      }
    }
    this.clearStash()
    await this.createSession()
  }

  async createSession() {
    this.showLoading()
    try {
      const res = await this.post(this.createUrlValue, {
        client_info: {
          device_name: navigator.userAgent.slice(0, 120),
          os:          this.detectOS()
        }
      })
      if (!res.ok) {
        this.showError(this.i18n("generic_error"))
        return
      }
      const body = await res.json()

      // Already-authenticated short-circuit from the server.
      if (body.redirect_to) {
        window.location.assign(body.redirect_to)
        return
      }

      this.session = {
        token:             body.session_token,
        secret:            body.session_secret,
        expires_at:        body.expires_at,
        poll_interval_ms:  this.clampPollMs(body.poll_interval * 1000),
        poll_url:          body.poll_url
      }
      this.saveStash(this.session)
      this.renderQR(body.qr_svg)
      this.showPending()
      this.startCountdown()
      this.schedulePoll()
    } catch (_e) {
      this.showError(this.i18n("network_error"))
    }
  }

  async pollOnce() {
    if (!this.session) return false
    try {
      const res = await fetch(this.session.poll_url, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (res.status === 404) {
        this.handleStatus("expired")
        return false
      }
      if (!res.ok) {
        this.handlePollError()
        return false
      }
      this.backoffMs = 0
      const body = await res.json()
      this.handleStatus(body.status)
      return true
    } catch (_e) {
      this.handlePollError()
      return false
    }
  }

  schedulePoll() {
    this.clearPoll()
    if (document.hidden) return
    const delay = this.backoffMs || (this.session?.poll_interval_ms ?? 1500)
    this.pollTimer = setTimeout(async () => {
      const terminal = ["consuming", "declined", "expired", "error"].includes(this.state)
      if (terminal) return
      await this.pollOnce()
      const stillPolling = ["pending", "scanned"].includes(this.state)
      if (stillPolling) this.schedulePoll()
    }, delay)
  }

  clearPoll() {
    if (this.pollTimer) clearTimeout(this.pollTimer)
    this.pollTimer = null
  }

  handleStatus(status) {
    switch (status) {
      case "pending":    this.showPending(); break
      case "scanned":    this.showScanned(); break
      case "authorized": this.consume(); break
      case "declined":   this.showTerminal("declined"); break
      case "expired":    this.showTerminal("expired"); break
      case "consumed":
        // Edge: we polled during/right after our own consume race — trust
        // the current view rather than flicker. If we never drove consume
        // ourselves (multi-tab?), treat as expired so user can retry.
        if (this.state !== "consuming") this.showTerminal("expired")
        break
      default:
        // Unknown → keep current state, try again next tick.
        break
    }
  }

  handlePollError() {
    // Exponential backoff without changing visible state — user still sees
    // the QR and can try to scan. If they succeed while we're between
    // polls, the next tick picks it up.
    this.backoffMs = this.backoffMs
      ? Math.min(this.backoffMs * 2, this.constructor.BACKOFF_MAX_MS)
      : this.constructor.BACKOFF_INITIAL_MS
  }

  async consume() {
    if (this.state === "consuming") return
    this.showConsuming()
    this.clearPoll()
    this.clearCountdown()
    try {
      const res = await this.post(this.consumeUrlValue, {
        session_token:  this.session.token,
        session_secret: this.session.secret
      })
      const body = await res.json().catch(() => ({}))
      if (res.ok && body.redirect_to) {
        this.clearStash()
        window.location.assign(body.redirect_to)
        return
      }
      // Server rejected — map common codes to terminal visuals.
      const code = body?.error?.code
      if (code === "expired" || code === "already_consumed") {
        this.showTerminal("expired")
      } else if (code === "account_suspended" || code === "account_pending_deletion") {
        this.showTerminal("declined", body.error.message)
      } else {
        this.showTerminal("expired", body?.error?.message)
      }
    } catch (_e) {
      this.showTerminal("expired", this.i18n("network_error"))
    }
  }

  restart() {
    this.clearPoll()
    this.clearCountdown()
    this.clearStash()
    this.session = null
    this.backoffMs = 0
    this.createSession()
  }

  // ------------- Visibility -------------

  onVisibilityChange() {
    if (document.hidden) {
      this.clearPoll()
      return
    }
    if (["pending", "scanned"].includes(this.state)) {
      this.pollOnce().then((ok) => { if (ok) this.schedulePoll() })
    }
  }

  // ------------- Rendering -------------

  showLoading() {
    this.state = "idle"
    this.hideAllOverlays()
    this.loadingTarget.classList.remove("hidden")
    this.loadingTarget.classList.add("flex")
    this.qrTarget.classList.add("hidden")
    this.countdownTarget.textContent = ""
    this.instructionsTarget.classList.remove("hidden")
  }

  renderQR(svg) {
    this.qrTarget.innerHTML = svg
    const inner = this.qrTarget.querySelector("svg")
    if (inner) {
      inner.setAttribute("width", "100%")
      inner.setAttribute("height", "100%")
      inner.style.display = "block"
    }
  }

  showPending() {
    this.state = "pending"
    this.hideAllOverlays()
    this.loadingTarget.classList.add("hidden")
    this.loadingTarget.classList.remove("flex")
    this.qrTarget.classList.remove("hidden")
    this.qrTarget.classList.add("flex")
    this.qrTarget.style.opacity = "1"
    this.instructionsTarget.classList.remove("hidden")
  }

  showScanned() {
    this.state = "scanned"
    this.hideAllOverlays()
    this.qrTarget.classList.remove("hidden")
    this.qrTarget.classList.add("flex")
    this.qrTarget.style.opacity = "0.35"
    this.scannedOverlayTarget.classList.remove("hidden")
    this.scannedOverlayTarget.classList.add("flex")
    this.instructionsTarget.classList.add("hidden")
  }

  showConsuming() {
    this.state = "consuming"
    this.hideAllOverlays()
    this.qrTarget.classList.remove("hidden")
    this.qrTarget.classList.add("flex")
    this.qrTarget.style.opacity = "0.15"
    this.consumingOverlayTarget.classList.remove("hidden")
    this.consumingOverlayTarget.classList.add("flex")
    this.instructionsTarget.classList.add("hidden")
    this.countdownTarget.textContent = ""
    this.countdownRowTarget.classList.add("invisible")
  }

  showTerminal(kind, overrideMessage = null) {
    this.state = kind
    this.clearPoll()
    this.clearCountdown()
    this.hideAllOverlays()
    this.qrTarget.classList.add("hidden")
    this.qrTarget.classList.remove("flex")
    this.terminalOverlayTarget.classList.remove("hidden")
    this.terminalOverlayTarget.classList.add("flex")
    this.instructionsTarget.classList.add("hidden")
    this.countdownRowTarget.classList.add("invisible")

    if (kind === "declined") {
      this.terminalIconTarget.className = "size-12 rounded-full flex items-center justify-center mb-3 bg-red-100"
      this.terminalIconTarget.innerHTML = `
        <svg class="size-6 text-red-600" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="m15 9-6 6"></path>
          <path d="m9 9 6 6"></path>
        </svg>`
      this.terminalMessageTarget.textContent = overrideMessage || this.i18n("declined")
    } else {
      // expired / default fallback
      this.terminalIconTarget.className = "size-12 rounded-full flex items-center justify-center mb-3 bg-gray-200"
      this.terminalIconTarget.innerHTML = `
        <svg class="size-6 text-gray-600" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>`
      this.terminalMessageTarget.textContent = overrideMessage || this.i18n("expired")
    }
  }

  showError(message) {
    this.showTerminal("expired", message)
  }

  hideAllOverlays() {
    for (const t of [this.scannedOverlayTarget, this.consumingOverlayTarget, this.terminalOverlayTarget]) {
      t.classList.add("hidden")
      t.classList.remove("flex")
    }
    this.countdownRowTarget.classList.remove("invisible")
  }

  // ------------- Countdown -------------

  startCountdown() {
    this.clearCountdown()
    this.updateCountdown()
    this.countdownTimer = setInterval(() => this.updateCountdown(), 1000)
  }

  clearCountdown() {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
    this.countdownTimer = null
  }

  updateCountdown() {
    if (!this.session?.expires_at) return
    const remaining = Math.max(0, Math.floor((new Date(this.session.expires_at) - Date.now()) / 1000))
    if (remaining <= 0) {
      this.handleStatus("expired")
      return
    }
    const mm = String(Math.floor(remaining / 60)).padStart(2, "0")
    const ss = String(remaining % 60).padStart(2, "0")
    this.countdownTarget.textContent = `${mm}:${ss} ${this.i18n("countdown_suffix")}`
  }

  // ------------- Storage -------------

  loadStash() {
    try {
      const raw = sessionStorage.getItem(this.constructor.STORAGE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch { return null }
  }

  saveStash(session) {
    try {
      sessionStorage.setItem(this.constructor.STORAGE_KEY, JSON.stringify(session))
    } catch { /* quota / privacy modes — silent fallback, flow still works */ }
  }

  clearStash() {
    try { sessionStorage.removeItem(this.constructor.STORAGE_KEY) } catch {}
  }

  isStashUsable(stash) {
    if (!stash?.token || !stash?.secret || !stash?.expires_at) return false
    return new Date(stash.expires_at) > new Date()
  }

  // ------------- Helpers -------------

  async post(url, body) {
    return fetch(url, {
      method:  "POST",
      headers: {
        "Content-Type":  "application/json",
        "Accept":        "application/json",
        "X-CSRF-Token":  this.csrfToken()
      },
      credentials: "same-origin",
      body: JSON.stringify(body || {})
    })
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  i18n(key) {
    return (this.i18nValue && this.i18nValue[key]) || ""
  }

  clampPollMs(ms) {
    const floor = this.constructor.POLL_FLOOR_MS
    const max   = this.constructor.POLL_MAX_MS
    if (!ms || Number.isNaN(ms)) return 1500
    return Math.min(Math.max(ms, floor), max)
  }

  detectOS() {
    const ua = navigator.userAgent
    if (/Mac/i.test(ua))     return "macOS"
    if (/Windows/i.test(ua)) return "Windows"
    if (/Linux/i.test(ua))   return "Linux"
    if (/Android/i.test(ua)) return "Android"
    if (/iP(hone|ad|od)/i.test(ua)) return "iOS"
    return "Unknown"
  }
}
